'use client'

import { observer } from 'mobx-react-lite'
import type { GetServerSidePropsContext, NextPage } from 'next'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import HeroCreateAccount from '@/components/v1/landingPage/heroCreateAccount'
import HeroLatestArticle from '@/components/v1/landingPage/heroLatestArticle'
import SectionContainer from '@/components/v1/ui/sectionContainer'
import { Article } from '@/types/Article'
import Link from 'next/link'
import { authetnicateGssp } from '@/utils/auth/authenticate-gssp'
import { PodcastGroup } from '@/types/Podcast'
import { DOMAIN_ACTION, SOCIAL_LINKS, ROUTES, PROD_URL } from '@/constants'
import axios from 'axios'
import { CurrentUser } from '../customer/dashboard/[slug]/dashboard'
import { down } from 'styled-breakpoints'
import { useBreakpoint } from 'styled-breakpoints/react-styled'
import { TileCarousel } from '@/components/v1/ui/tile-carousel/tile-carousel'
import ArticleGridItem from '@/components/v1/article-gird-item/article-grid-item'
import Head from 'next/head'
import { api } from '@/api'
import { ArticleStatus } from '@/types/api/article'

const PodcastsContainer = styled.section`
  overflow: hidden;
`
const PodcastsWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto 0 auto;

  .rec-slider-container {
    overflow: visible;
  }

  h4 {
    margin: 20px 0;
  }

  .rec.rec-carousel {
    width: 100%;
  }

  .rec-carousel-wrapper {
    position: relative;
    & > div:nth-child(2) {
      margin-top: 0;
    }
  }

  ${down('sm')} {
    margin: 40px 0 0 20px;
    padding: 0 20px 0 0;
    width: 92%;

    .rec-slider-container {
      margin: 0;
    }

    .rec-carousel-wrapper {
      & > div:nth-child(2) {
        margin-top: 30px;
      }
    }
  }
`
const PodcastsGroup = styled.div`
  margin: 50px auto 0 auto;
`

const H4Wrapper = styled.h4`
  text-align: center;
  font-size: 26px;
  font-family: Oxygen;
  font-style: normal;
  font-weight: 700;
  line-height: 30px;
  text-transform: uppercase;
  margin: 25px 0;
`

const ParagWrapper = styled.p`
  text-align: center;
  font-size: 18px;
  font-family: Oxygen;
  font-style: normal;
  font-weight: 400;
  line-height: 26px;
  margin: 25px 0;
  a {
    text-decoration: underline;
  }
`
const SpanWrapper = styled.span`
  text-align: center;
  font-size: 18px;
  font-family: Oxygen;
  font-style: normal;
  font-weight: bold;
  line-height: 26px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 25px 0;
`
const ParagSeparator = styled.hr`
  margin: -15px 0 25px 0;
`

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const { user } = await authetnicateGssp(ctx)
  const [err, podcasts] = await api.be.article.getPodcastList()
  if (err) throw err

  return {
    props: {
      user,
      podcasts,
    },
  }
}

interface PodcastProps {
  user: CurrentUser
  podcasts: PodcastGroup[]
}

const PodcastPage: NextPage<PodcastProps> = ({ podcasts, user }) => {
  const router = useRouter()
  const isMobile = useBreakpoint(down('md'))
  const userIsLoggedIn = !!user

  const article: Article = {
    id: 1,
    path: '',
    title: '',
    article_tags: [],
    description: '',
    hero_image_ref: {
      id: 1,
      url: `${PROD_URL}/assets/images/podcast.png`,
      name: '',
      prefix: '',
      description: '',
      slug: '',
      author: '',
      alt: 'podcast_image',
      created_at: '',
      updated_at: '',
    },
    created_at: '',
    created_by: 1,
    status: ArticleStatus.PUBLISHED,
    hero_image: 1,
    is_featured_article: false,
    trending: false,
    updated_at: '',
    sponsored: false,
    feature_suppliers: [],
    article_blocks: [],
    article_categories: null,
    article_subcategories: null,
  }
  return (
    <>
      <Head>
        <title>Wedding Podcast by Rock My Wedding</title>
        <meta
          name="description"
          content="The Rock My Wedding Podcast will help you navigate the world of wedding planning discussing everything from guest lists, to decor trends to finding THE dress."
        />
      </Head>
      <SectionContainer>
        <HeroLatestArticle
          title={'Welcome to the podcast show notes'}
          description={
            'Join Imogen, Ella, and a whole host of guests on our wedding podcast. Topics of conversation include bridesmaids, guest lists, flowers, fashion, and defining your sense of style whilst planning your wedding. And loads more. You can find all the topics and links to episodes right here on the show notes page!'
          }
          smallLabel=""
          showLabel={false}
          article={article}
        />
      </SectionContainer>
      <SectionContainer>
        <H4Wrapper>ROCK MY WEDDING THE PODCAST</H4Wrapper>
        <ParagWrapper>
          Hello and welcome! You&apos;ve stumbled across the Rock My Wedding
          Podcast (lucky you). If you&apos;re looking for all that wedding
          inspiration and planning advice, you&apos;ve come to the right place.
          With help from our incredible{' '}
          <a onClick={() => router.push('/supplier')}>Recommended suppliers,</a>{' '}
          we&apos;re discussing everything from bridesmaid fashion to finding
          your wedding venue, to budgeting. With 40 episodes from Becky &
          Charlotte and new episodes from Imogen & Ella, you&apos;ve got so much
          to choose from you&apos;re simply spoilt for choice! It&apos;s also
          one of the best ways to get important wedding planning information on
          the go. Because who can scroll pages whilst walking the dog or cooking
          dinner? Instead, you can just put us on and... listen. There are also
          show-note articles to support every episode, which means if reading is
          more your thing, then we&apos;ve got you covered. Our new episodes are
          also filmed, so you can take a peek at our faces and see what fancy
          locations we record the podcast at if you fancy it… or don&apos;t, we
          won&apos;t mind.
        </ParagWrapper>
        <SpanWrapper>HOW CAN I LISTEN?</SpanWrapper>
        <ParagWrapper>
          Glad you asked! There are loads of ways that you can listen. If you
          have an iPhone, check us out on the{' '}
          <a href="https://podcasts.apple.com/gb/podcast/rock-my-wedding-the-podcast/id1436374349">
            Apple Podcasts
          </a>{' '}
          app. You can find us on{' '}
          <a href="https://open.spotify.com/show/0YheAsYO94AHyH2Oq24k4A">
            Spotify here.
          </a>{' '}
          We&apos;re also available on{' '}
          <a href="https://podcasts.google.com/feed/aHR0cHM6Ly9hbmNob3IuZm0vcy81YTk3Y2MwL3BvZGNhc3QvcnNz">
            Google podcasts,
          </a>{' '}
          and wherever you usually listen to podcasts. Just search for Rock My
          Wedding.
          <br></br>
          <br></br>
          We hope you love listening and if you ever want to suggest episodes
          for us to cover (or if you are a supplier who would like to feature)
          just drop us a message at{' '}
          <a href="mailto:podcast@rockmywedding.co.uk">
            podcast@rockmywedding.co.uk
          </a>
          .<br></br>
          <br></br>
          Choose an episode below to start listening.
          <br></br>
          <br></br>
        </ParagWrapper>
        <ParagSeparator />
      </SectionContainer>
      <PodcastsContainer>
        <PodcastsWrapper>
          {podcasts.map((podcastGroup, i) => (
            <PodcastsGroup key={i}>
              <H4Wrapper>{podcastGroup.title}</H4Wrapper>

              <TileCarousel
                dynamicCarouselProps={{
                  itemPadding: [0, 10, 20, 10],
                  initialActiveIndex: isMobile ? 1 : 2,
                  breakPoints: [
                    { width: 1, itemsToShow: 1, itemsToScroll: 1 },
                    { width: 300, itemsToShow: 2, itemsToScroll: 2 },
                    { width: 768, itemsToShow: 3, itemsToScroll: 3 },
                  ],
                  pagination: !isMobile,
                }}
              >
                {podcastGroup.articles.map((podcast, i) => (
                  <ArticleGridItem
                    article={{
                      id: podcast.id,
                      path: podcast.url,
                      title: podcast.title,
                      article_tags: [],
                      hero_image_ref: {
                        url: podcast.hero_image,
                        alt: '',
                      },
                    }}
                    key={i}
                  ></ArticleGridItem>
                ))}
              </TileCarousel>
            </PodcastsGroup>
          ))}
        </PodcastsWrapper>
      </PodcastsContainer>
      <SectionContainer>
        <H4Wrapper>
          I&apos;VE LISTENED TO EVERYTHING! WHAT&apos;S NEXT?
        </H4Wrapper>
        <ParagWrapper>
          Check out the <Link href={ROUTES.WEDDING_PLANNING}>Planning</Link> and{' '}
          <Link href={ROUTES.WEDDING_INSPIRATION}>Inspiration</Link>, sections
          of our site for even more free wedding planning content. You can
          filter real weddings by budget, venue type, location, and more to find
          the exact type of weddings you want to see. And in our Planning
          section, you can find out all the information you need about wedding
          readings, how to look after your dress, how to create your own amazing
          DIY decor and you guessed it... loads more.
          <br></br>
          <br></br>And don&apos;t forget you can follow us on{' '}
          <Link href={SOCIAL_LINKS.INSTAGRAM}>Instagram</Link> too and over on{' '}
          <Link href={SOCIAL_LINKS.PINTEREST}>Pinterest</Link> for all of your
          digital scrapbooking needs.
        </ParagWrapper>
        <H4Wrapper>
          I&apos;M TRYING TO REDUCE MY SCREEN TIME. I WISH YOU HAD A BOOK!
        </H4Wrapper>
        <ParagWrapper>
          Lucky for you, we do! You can get a copy of our book &apos;Your Day
          Your Way&apos; (which was written by Charlotte who you will be
          familiar with from the podcast so you&apos;re guaranteed to love it)
          from your favourite high street book stores, including{' '}
          <Link
            href={
              'https://www.waterstones.com/book/rock-my-wedding/charlotte-oshea/9781785033537'
            }
          >
            Waterstones
          </Link>
          . You can also find it on{' '}
          <Link
            href={
              'https://www.amazon.co.uk/Rock-My-Wedding-Your-Day/dp/1785033530'
            }
          >
            Amazon
          </Link>
          .<br></br>
          <br></br>
          The book makes a great gift for newly engaged friends too. Imagine how
          popular you will be with them! &apos;Hey *insert friends name*,
          there&apos;s this podcast and this excellent{' '}
          <Link
            href={
              'https://www.amazon.co.uk/Rock-My-Wedding-Your-Day/dp/1785033530'
            }
          >
            book
          </Link>{' '}
          and all this website{' '}
          <Link href={ROUTES.WEDDING_INSPIRATION}>inspiration</Link> and this{' '}
          <Link href={ROUTES.AUTH.SIGN_UP}>dashboard</Link> where you have your
          own planner and the <Link href={'/supplier'}>suppliers</Link> are all
          incredible!&apos;
          <br></br>
          <br></br>
          You&apos;re welcome.
          <br></br>
          <br></br>
          <br></br>
        </ParagWrapper>
        <ParagSeparator />
        <ParagWrapper>
          Thanks to all the guests who are involved in making the podcast so
          great.
          <br></br>
          <br></br>A big shout-out goes to Recommended suppliers, We Are // The
          Clarkes who produce the podcast (as well as being incredibly talented
          photographers and videographers). And Recommended suppliers, The
          Wedding Shop who are sponsoring our Episodes from January 2023
        </ParagWrapper>
      </SectionContainer>
      {!userIsLoggedIn && (
        <SectionContainer>
          <HeroCreateAccount />
        </SectionContainer>
      )}
    </>
  )
}

export default observer(PodcastPage)
